import React, { useMemo, useState, useEffect, useRef } from 'react';
import { 
  LayoutDashboard, Users, UserSquare2, Building2, Wrench,
  Smartphone, Monitor, UserCog, FileText, Database, Trash2, 
  Settings, CreditCard, PenTool, Map, Lock, Moon, Sun, LogOut,
  ChevronLeft, ChevronRight, Calendar, Camera, LoaderCircle, Mail, Phone, BadgeCheck, X, BookOpen, Megaphone, Cpu
} from 'lucide-react';
import { useDispatch, useSelector } from 'react-redux';
import { resetAuthState, selectUser, selectUserName, selectUserRole, setUser } from '../../features/auth/loginSlice';
import { checkDealerPayment, getSidebarCategories } from '../../features/sidebar/sidebarSlice';
import { loginAPI } from '../../features/auth/loginAPI';
import './sidebar.css';

const ICONS = {
  LayoutDashboard,
  Users,
  UserSquare2,
  Building2,
  Wrench,
  Smartphone,
  Monitor,
  UserCog,
  FileText,
  Database,
  Trash2,
  Settings,
  CreditCard,
  PenTool,
  Map,
  Lock,
  Calendar,
  BookOpen,
  Megaphone,
  Cpu,
};

const ROUTE_ALIASES = {
  '/dashboard': '/dashboard',
  '/clients': '/clients',
  '/office_logs': '/clients',
  '/dealers': '/dealers',
  '/offices': '/offices',
  '/technician': '/technician',
  '/devices': '/devices',
  '/production-dashboard': '/production-dashboard',
  '/production-devices': '/production-devices',
  '/oleddevices': '/oleddevices',
  '/clientofficer': '/clientofficer',
  '/removals': '/removals',
  '/firmwares': '/firmwares',
  '/management': '/management',
  '/calibrationkit': '/calibrationkit',
  '/lockoutstatus': '/lockoutstatus',
  '/geo-fencing': '/geo-fencing',
  '/reports': '/reports',
  '/payments': '/payments',
  '/autopay': '/autopay',
  '/appointments': '/appointments',
  '/announcement': '/announcement',
};

const formatLabel = (label) => label
  .toLowerCase()
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (char) => char.toUpperCase());

const getUserState = (user) => user?.state || user?.dealerState || user?.snostate || null;

const getPaymentCustomerId = (user) => (
  user?.customer_id ||
  user?.customerId ||
  user?.stripeCustomerId ||
  user?.dealerId ||
  user?.dealerid ||
  null
);

const normalizeSidebarCategories = (categories) => categories
  .map((category) => ({
    ...category,
    items: category.items
      .map((item) => {
        const normalizedPath = ROUTE_ALIASES[item.path] || item.path;
        const Icon = typeof item.icon === 'string' ? ICONS[item.icon] : item.icon;

        if (!ROUTE_ALIASES[item.path] || !Icon) {
          return null;
        }

        return {
          ...item,
          icon: Icon,
          path: normalizedPath,
          label: formatLabel(item.label),
        };
      })
      .filter(Boolean),
  }))
  .filter((category) => category.items.length > 0);

const Sidebar = ({
  onNavigate,
  currentPath,
  isMobileOpen,
  onClose,
  toggleTheme,
  isDarkMode
}) => {
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 992);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDealerDetailsOpen, setIsDealerDetailsOpen] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoMessage, setLogoMessage] = useState(null);
  const [dealerDetails, setDealerDetails] = useState(null);
  const [showAllPhones, setShowAllPhones] = useState(false);
  const fileInputRef = useRef(null);
  const dispatch = useDispatch();

  const user = useSelector(selectUser);
  const userName = useSelector(selectUserName);
  const userRole = useSelector(selectUserRole);

  const grantAccess = useSelector((state) => state.sidebar.grantAccess);

  const displayUserName = dealerDetails?.dealerName || userName || 'AlcobrakeCA';
  const displayUserRole = userRole || 'Admin';
  const avatarInitial = displayUserName.charAt(0).toUpperCase();
  const sidebarCategories = useMemo(() => {
    const roleCategories = getSidebarCategories(
      userRole,
      grantAccess,
      getUserState(user),
      user?.emailId || user?.email
    );
    return normalizeSidebarCategories(roleCategories);
  }, [grantAccess, user, userRole]);

  const isDealer = ['dealer', 'superadmin', 'admin', 'Admin'].includes(userRole);
  const dealerLogo = dealerDetails?.dealerlogo || user?.dealerlogo;
  const collapsedLogo = `${process.env.PUBLIC_URL}/${String(user?.dealerId) === '31261' ? 'favicon.ico' : 'favicon1.ico'}`;
  const expandedLogo = dealerLogo || `${process.env.PUBLIC_URL}/alcobrake1.jpg`;
  const dealerEmail = dealerDetails?.dealerEmail || user?.emailId;
  const dealerPhoneValue = dealerDetails?.dealerMobile1 || dealerDetails?.dealerMobile2 || user?.dealerMobile || user?.dealerMobile1 || user?.mobile || user?.mobileNumber || user?.phone || user?.phoneNumber;
  const dealerPhones = String(dealerPhoneValue || '').split(',').map((phone) => phone.trim()).filter(Boolean);
  const dealerPhone = dealerPhones[0];

  useEffect(() => {
    let active = true;
    if (!isDealer || !user?.dealerId) {
      setDealerDetails(null);
      return undefined;
    }
    loginAPI.getDealerDetails(user.dealerId)
      .then((details) => { if (active) setDealerDetails(details); })
      .catch((error) => console.error('Unable to load dealer details:', error));
    return () => { active = false; };
  }, [isDealer, user?.dealerId]);

  useEffect(() => {
    const favicon = document.querySelector("link[rel~='icon']");
    if (favicon) {
      favicon.href = `${process.env.PUBLIC_URL}/${String(user?.dealerId) === '31261' ? 'favicon.ico' : 'favicon1.ico'}`;
    }
  }, [user?.dealerId]);

  const handleProfileClick = () => {
    if (!isDealer || isCollapsed) return;
    setLogoMessage(null);
    setShowAllPhones(false);
    setIsDealerDetailsOpen((open) => !open);
  };

  const triggerFileUpload = () => {
    if (logoUploading) return;
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setLogoMessage({ type: 'error', text: 'Please select an image file.' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setLogoMessage({ type: 'error', text: 'Please select an image smaller than 5 MB.' });
      return;
    }

    setLogoUploading(true);
    setLogoMessage(null);
    try {
      const updatedUser = await loginAPI.updateDealerLogo(user, file);
      dispatch(setUser(updatedUser));
      const updatedDealer = await loginAPI.getDealerDetails(user.dealerId);
      setDealerDetails(updatedDealer);
      setLogoMessage({ type: 'success', text: 'Dealer logo updated.' });
    } catch (error) {
      console.error('Unable to update dealer logo:', error);
      setLogoMessage({ type: 'error', text: 'Unable to update the logo. Please try again.' });
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogout = () => {
    dispatch(resetAuthState());
    if (onNavigate) {
      onNavigate('/login');
    }
    if (!isDesktop && onClose) {
      onClose();
    }
  };

  useEffect(() => {
    const handleResize = () => {
      setIsDesktop(window.innerWidth >= 992);
      if (window.innerWidth < 992) {
        setIsCollapsed(false); // Mobile sidebar is never "collapsed", just hidden/shown
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const customerId = getPaymentCustomerId(user);

    if (userRole === 'dealer' && customerId) {
      dispatch(checkDealerPayment(customerId));
    }
  }, [dispatch, user, userRole]);

  const handleNavClick = (path) => {
    if (onNavigate) onNavigate(path);
    if (!isDesktop && onClose) {
      onClose();
    }
  };

  const isActivePath = (path) => currentPath === path;
  
  // Calculate sidebar classes
  const mobileClass = !isDesktop && isMobileOpen ? 'sidebar--mobile-open' : 'sidebar--mobile-closed';
  const collapseClass = isDesktop && isCollapsed ? 'sidebar--collapsed' : '';

  return (
    <>
      <aside className={`sidebar ${mobileClass} ${collapseClass}`}>
        {/* Header / Logo Area */}
       <div className="sidebar__header">
  <div className="sidebar__logo-container">

  <div className="sidebar__logo-container">
  <img
    src={
      isCollapsed
        ? collapsedLogo
        : expandedLogo
    }
    alt="Logo"
    onError={(event) => {
      event.currentTarget.onerror = null;
      event.currentTarget.src = `${process.env.PUBLIC_URL}/alcobrake1.jpg`;
    }}
    className={`sidebar__logo-image ${
      isCollapsed ? 'sidebar__logo-image--collapsed' : ''
    }`}
  />
 </div>

  </div>

  {isDesktop && (
    <button
      className="sidebar__collapse-btn"
      onClick={() => setIsCollapsed(!isCollapsed)}
    >
      {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
    </button>
  )}

  {!isDesktop && (
    <button className="sidebar__collapse-btn" onClick={onClose}>
      <ChevronLeft size={18} />
    </button>
  )}
</div>

        {/* Navigation Links */}
        <div className="sidebar__scroll-area">
          {sidebarCategories.map((category, idx) => (
            <div key={idx} className="sidebar__category">
              {!isCollapsed && (
                <div className="sidebar__category-title">{category.title}</div>
              )}
              {isCollapsed && <div className="sidebar__category-divider" />}
              
              <ul className="sidebar__nav-list">
                {category.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActivePath(item.path);
                  return (
                    <li key={item.label}>
                      <button
                        className={`sidebar__nav-item ${active ? 'sidebar__nav-item--active' : ''}`}
                        onClick={() => handleNavClick(item.path)}
                        title={isCollapsed ? item.label : ''}
                      >
                        <Icon size={20} className="sidebar__nav-icon" />
                        {!isCollapsed && <span className="sidebar__nav-label">{item.label}</span>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>

        {/* User Profile Footer */}
        <div className="sidebar__footer">
          {isDealerDetailsOpen && isDealer && !isCollapsed && (
            <div className="sidebar__dealer-card" role="dialog" aria-label="Dealer details">
              <button className="sidebar__dealer-close" onClick={() => setIsDealerDetailsOpen(false)} aria-label="Close dealer details">
                <X size={16} />
              </button>
              <button className="sidebar__dealer-logo" onClick={triggerFileUpload} disabled={logoUploading} title="Upload a new dealer logo">
                {dealerLogo ? <img src={dealerLogo} alt={`${displayUserName} logo`} /> : <span>{avatarInitial}</span>}
                <span className="sidebar__dealer-logo-action">
                  {logoUploading ? <LoaderCircle size={16} className="sidebar__spinner" /> : <Camera size={16} />}
                </span>
              </button>
              <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileChange} />
              <div className="sidebar__dealer-heading">
                <strong>{displayUserName}</strong>
                <span><BadgeCheck size={14} /> { userRole }</span>
              </div>
              <div className="sidebar__dealer-details">
                <span><Mail size={15} />{dealerEmail || 'Email unavailable'}</span>
                <div className="sidebar__dealer-phone-row">
                  <Phone size={15} />
                  <div className="sidebar__dealer-phone-list">
                    {(showAllPhones ? dealerPhones : dealerPhones.slice(0, 1)).map((phone) => <span key={phone}>{phone}</span>)}
                    {!dealerPhone && <span>Phone unavailable</span>}
                    {dealerPhones.length > 1 && (
                      <button type="button" onClick={() => setShowAllPhones((shown) => !shown)}>
                        {showAllPhones ? 'Show less' : `+${dealerPhones.length - 1} more`}
                      </button>
                    )}
                  </div>
                </div>
                {/* <span className="sidebar__dealer-id">Dealer ID&nbsp; {user?.dealerId || '—'}</span> */}
              </div>
              {logoMessage && <div className={`sidebar__logo-message sidebar__logo-message--${logoMessage.type}`}>{logoMessage.text}</div>}
            </div>
          )}
          <button className={`sidebar__profile ${isDealer ? 'sidebar__profile--clickable' : ''}`} onClick={handleProfileClick} aria-expanded={isDealerDetailsOpen}>
            <div className="sidebar__avatar">
              {avatarInitial}
            </div>
            {!isCollapsed && (
              <div className="sidebar__user-info">
                <div className="sidebar__user-name">{displayUserName}</div>
                <div className="sidebar__user-role">{displayUserRole}</div>
              </div>
            )}
          </button>
          
          <div className={`sidebar__footer-actions ${isCollapsed ? 'sidebar__footer-actions--vertical' : ''}`}>
            <button 
              className="sidebar__action-btn" 
              onClick={toggleTheme}
              title={isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
            >
              {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button className="sidebar__action-btn" title="Logout" onClick={handleLogout}>
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {!isDesktop && isMobileOpen && (
        <div className="sidebar-overlay" onClick={onClose} />
      )}
    </>
  );
};

export default Sidebar;
